import advanceReportingConstants from "@/constants/advanceReportingConstants"

/**
 * Chart Feature Flags
 * Controls visibility of charts across the entire Advanced Reporting module.
 * Set to `false` to disable a chart (overrides backend settings).
 */
const { DEFAULT_CHART_IDS } = advanceReportingConstants

const CHART_FEATURE_FLAGS = Object.freeze({
  [DEFAULT_CHART_IDS.USERS_KPI]: true,
  [DEFAULT_CHART_IDS.SCHOOL_YEAR_PASS_BREAKDOWN]: true,
  [DEFAULT_CHART_IDS.SCHOOL_YEAR_PASS_TREND]: true,
  [DEFAULT_CHART_IDS.PASS_BREAKDOWN]: true,
  [DEFAULT_CHART_IDS.PASS_TREND]: true,
  [DEFAULT_CHART_IDS.TOTAL_PASSES_BY_SCHOOLS]: true,
  [DEFAULT_CHART_IDS.MOST_PASSES_TAKEN]: true,
  [DEFAULT_CHART_IDS.MOST_PASSES_GRANTED]: true,
  [DEFAULT_CHART_IDS.PASS_BREAKDOWN_BY_GRAD_YEAR]: false,
  [DEFAULT_CHART_IDS.AVERAGE_PASS_DURATION]: false,
  [DEFAULT_CHART_IDS.MOST_FREQUENTED_LOCATIONS]: true,
  [DEFAULT_CHART_IDS.TARDY_INFRACTIONS]: true,
  [DEFAULT_CHART_IDS.CONTACT_CONTROL]: true,
  [DEFAULT_CHART_IDS.VISITORS_TREND]: true,
  [DEFAULT_CHART_IDS.ZERO_GRANTERS]: true
})

/**
 * School IDs for which specific charts are enabled (override global flags).
 * Charts listed here are shown as enabled when any of these schools are selected.
 */
const SCHOOL_CHART_ENABLED_OVERRIDES = Object.freeze({
  6500: [
    DEFAULT_CHART_IDS.PASS_BREAKDOWN_BY_GRAD_YEAR,
    DEFAULT_CHART_IDS.AVERAGE_PASS_DURATION
  ],
  5700: [
    DEFAULT_CHART_IDS.PASS_BREAKDOWN_BY_GRAD_YEAR,
    DEFAULT_CHART_IDS.AVERAGE_PASS_DURATION
  ]
})

/**
 * Check if a chart is enabled via feature flags
 * @param {string} chartId - The frontend chart ID (from DEFAULT_CHART_IDS)
 * @param {number|number[]} [selectedSchoolIds] - Optional school ID or array of selected school IDs; when provided, school-specific overrides apply
 * @returns {boolean} - True if enabled, false if disabled
 */
const isChartEnabled = (chartId, selectedSchoolIds) => {
  const baseEnabled = CHART_FEATURE_FLAGS[chartId] !== false
  if (baseEnabled) return true

  const ids = Array.isArray(selectedSchoolIds)
    ? selectedSchoolIds
    : selectedSchoolIds != null
      ? [selectedSchoolIds]
      : []
  if (ids.length === 0) return false

  const enabledForSchool = ids.some(
    (schoolId) =>
      SCHOOL_CHART_ENABLED_OVERRIDES[schoolId]?.includes(chartId) === true
  )
  return enabledForSchool
}

const pages = {
  mainPage() {
    return {
      name: "Advanced Reporting",
      i18n_name_key: "sidebar_items.advanced_reporting",
      path: advanceReportingConstants.PATHS.REPORT,
      roles: advanceReportingConstants.ADVANCE_REPORTING_ADMIN_ROLES,
      helpKey: "advance_reporting_overview_v0004",
      dependency: "routerDependencies/hasAdvanceReportingAccess"
    }
  },
  scheduledReportsPage() {
    return {
      name: "Scheduled Reports",
      i18n_name_key: "sidebar_items.scheduled_reports",
      path: advanceReportingConstants.PATHS.SCHEDULED_REPORTS,
      roles: advanceReportingConstants.ADVANCE_REPORTING_ADMIN_ROLES,
      helpKey: "advance_reporting_scheduled_reports_v0002",
      dependency: "routerDependencies/hasAdvanceReportingAccess"
    }
  }
}

export default {
  pages,
  CHART_FEATURE_FLAGS,
  isChartEnabled
}
